import React from 'react'
import i18n from 'i18n'
import BLOCK from 'common/constants/BlockTypes/BlocksEnum'
import { IMAGE_SIZES } from 'common/constants/image.js'

import { CONDITIONS, hasPermission } from 'common/utils/permissions'

import DemoContent from './Panels/Control/Controls/DemoWithModal/components/DemoContent.js'

const PRIVACY_GAMIFICATION_DEMO = [
    {
        isVisible: () => !hasPermission(CONDITIONS.CAN_USE_GAMIFICATION),
        value: 'privacyDemo',
        tooltipText: <DemoContent isPrivacy />,
    },
    {
        isVisible: () => !hasPermission(CONDITIONS.CAN_USE_GAMIFICATION),
        value: 'gamificationDemo',
        tooltipText: <DemoContent />,
    },
]
const TIMER_CONTROLS = [
    {
        isVisible: () => !hasPermission(CONDITIONS.CAN_USE_GAMIFICATION),
        values: [
            {
                value: 'enableTimer',
            },
            {
                isVisible: blockData => blockData.enableTimer,
                value: 'timerType',
            },
            {
                isVisible: blockData =>
                    blockData.enableTimer && blockData.timerType && blockData.timerType.value === 'countdown',
                value: 'countdownTime',
            },
            {
                isVisible: blockData =>
                    blockData.enableTimer && blockData.timerType && blockData.timerType.value === 'countdown',
                value: 'timeIsUpScreen',
            },
        ],
    },
]
const LEAD_FORM_CONTROLS = [
    {
        isVisible: () => hasPermission(CONDITIONS.CAN_USE_LEAD_FORM_IN_BLOCKS),
        values: [
            { value: 'isShowLeadForm' },
            {
                isVisible: blockData => blockData.isShowLeadForm,
                value: 'leadFormStruct',
            },
        ],
    },
]
const GAMIFICATION_CONTROLS = [
    {
        isVisible: () => hasPermission(CONDITIONS.CAN_USE_GAMIFICATION),
        values: [
            {
                value: 'passwordList',
            },
        ],
    },
    {
        isVisible: () => hasPermission(CONDITIONS.CAN_USE_GAMIFICATION),
        label: i18n.t('Gamification'),
        values: [
            {
                value: 'isEnableRating',
            },
            {
                isVisible: blockData => blockData.isEnableRating,
                value: 'numberOfAttempts',
            },
            {
                isVisible: blockData => blockData.isEnableRating,
                tooltipText: `Example: <a href="https://interacty.me">Terms of use</a>`,
                value: 'legalStatement',
            },
            {
                isVisible: blockData => blockData.isEnableRating,
                value: 'isShowRatingEmail',
            },
            {
                value: 'enableTimer',
            },
            {
                isVisible: blockData => blockData.enableTimer,
                value: 'timerType',
            },
            {
                isVisible: blockData =>
                    blockData.enableTimer && blockData.timerType && blockData.timerType.value === 'stopwatch',
                value: 'enableStars',
            },
            {
                isVisible: blockData =>
                    blockData.enableTimer &&
                    blockData.timerType &&
                    blockData.timerType.value === 'stopwatch' &&
                    blockData.enableStars,
                descriptionText: i18n.t(`You can specify several separated by commas: 45, 30, 15`),
                value: 'starsTimeList',
            },
            {
                isVisible: blockData =>
                    blockData.enableTimer && blockData.timerType && blockData.timerType.value === 'countdown',
                value: 'countdownTime',
            },
            {
                isVisible: blockData =>
                    blockData.enableTimer && blockData.timerType && blockData.timerType.value === 'countdown',
                value: 'timeIsUpScreen',
            },
        ],
    },
]
const ACTION_BUTTON_CONTROLS = [
    {
        values: [
            { value: 'isActionButton' },
            {
                isVisible: blockData => !!blockData.isActionButton,
                value: 'actionButtonText',
            },
            {
                isVisible: blockData => !!blockData.isActionButton,
                value: 'actionButtonLink',
            },
            {
                isVisible: blockData => !!blockData.isActionButton,
                value: 'actionButtonColor',
            },
        ],
    },
]
const OLD_ACTION_BUTTON_CONTROLS = [
    {
        values: [
            { value: 'callToActionEnabled' },
            {
                isVisible: blockData => !!blockData.callToActionEnabled,
                value: 'callToActionText',
            },
            {
                isVisible: blockData => !!blockData.callToActionEnabled,
                value: 'callToActionLink',
            },
        ],
    },
]
const PINS_CONTROLS = [
    {
        values: [{ value: 'h' }, { value: 'd' }],
    },
    {
        values: [{ value: 'i' }],
    },
    {
        values: [{ value: 'blink' }, { value: 'btext' }],
    },
]

const CONTROLS = {
    app: [
        {
            values: [
                {
                    tooltipText: i18n.t(`Value in pixels (px). Applies in preview mode and in the published project`),
                    value: 'maxWidth',
                },
            ],
        },
        {
            values: [
                {
                    tooltipText: i18n.t(`Applies in preview mode and in the published project`),
                    value: 'isTransparentBackground',
                },
                {
                    isVisible: blockData => !blockData.isTransparentBackground,
                    value: 'bg',
                },
            ],
        },
        {
            values: [
                {
                    tooltipText: i18n.t(`Read the instructions on how it works`),
                    tooltipCallback: () => {
                        window.open(
                            'https://ask.interacty.me/question/1334486917311369216/how-can-i-add-different-languages-into-my-project',
                        )
                    },
                    value: 'customTranslations',
                },
            ],
        },
        {
            isVisible: () => hasPermission(CONDITIONS.CAN_USE_CUSTOM_PROJECT_STYLES),
            values: [{ value: 'customStyles' }],
        },
    ],
    blocks: {
        [BLOCK.text]: [
            {
                values: [{ value: 'wP_bg' }],
            },
        ],
        [BLOCK.image]: [
            {
                values: [
                    { value: 'url' },
                    { value: 'imageSize' },
                    {
                        isVisible: blockData => blockData.imageSize.value === IMAGE_SIZES.custom.value,
                        value: 'width',
                    },
                    {
                        isVisible: blockData =>
                            blockData.imageSize.value === IMAGE_SIZES.compact.value ||
                            blockData.imageSize.value === IMAGE_SIZES.custom.value,
                        value: 'position',
                    },
                ],
            },
            {
                label: i18n.t('Effects'),
                values: [{ value: 'blur' }],
            },
        ],
        [BLOCK.logo]: [
            {
                values: [{ value: 'url' }, { value: 'width' }, { value: 'padding' }, { value: 'position' }],
            },
            {
                values: [{ value: 'link' }],
            },
        ],
        [BLOCK.embedInteractyProject]: [
            {
                values: [{ value: 'embedCode' }],
            },
        ],
        [BLOCK.flipCards]: [
            {
                values: [{ value: 'imageProportions' }],
            },
            {
                values: [{ value: 'frontSrc' }, { value: 'backSrc' }],
            },
            {
                values: [{ value: 'frontColor' }, { value: 'backColor' }],
            },
        ],
        [BLOCK.youtubeVideo]: [
            {
                values: [{ value: 'embedCode' }],
            },
        ],
        [BLOCK.button]: [
            {
                values: [{ value: 'href' }, { value: 'brad' }, { value: 'bc' }, { value: 'pulse' }],
            },
        ],
        [BLOCK.interactiveImage]: [
            {
                values: [{ value: 'bimg' }],
            },
            {
                values: [{ value: 'count' }, { value: 'psize' }, { value: 'pimg' }, { value: 'pcl' }],
            },
            {
                values: [{ value: 'btcolor' }],
            },
        ],
        [BLOCK.interactiveImage + '.pins']: PINS_CONTROLS,
        [BLOCK.hiddenObjects]: [
            {
                values: [{ value: 'bimg' }, ...PRIVACY_GAMIFICATION_DEMO],
            },
            {
                values: [{ value: 'count' }, { value: 'psize' }, { value: 'pimg' }, { value: 'pcl' }],
            },
            {
                values: [{ value: 'btcolor' }],
            },
            {
                values: [
                    {
                        value: 'isShowCover',
                    },
                    {
                        isVisible: blockData => blockData.isShowCover,
                        value: 'coverHeader',
                    },
                    {
                        isVisible: blockData => blockData.isShowCover,
                        value: 'coverBtnText',
                    },
                ],
            },
            {
                collapsible: true,
                values: [
                    { value: 'suct' },
                    { value: 'sucd' },
                    { value: 'sucImg' },
                    { value: 'sucBlink' },
                    { value: 'sucBtext' },
                ],
            },
            ...GAMIFICATION_CONTROLS,
            ...LEAD_FORM_CONTROLS,
        ],
        [BLOCK.hiddenObjects + '.pins']: PINS_CONTROLS,
        [BLOCK.quiz]: [
            {
                values: [{ value: 'colorTheme' }],
            },
            {
                values: [{ value: 'progressBar' }, { value: 'showScores' }],
            },
            ...LEAD_FORM_CONTROLS,
            ...OLD_ACTION_BUTTON_CONTROLS,
        ],
        [BLOCK.thenNow]: [
            {
                values: [{ value: 'imageProportions' }],
            },
            {
                values: [{ value: 'leftSrc' }, { value: 'rightSrc' }],
            },
        ],
        [BLOCK.timeline]: [
            {
                values: [{ value: 'wP_bg' }, { value: 'imageUrl' }, { value: 'imageDescription' }],
            },
        ],
        [BLOCK.memoryCards]: [
            {
                values: [
                    {
                        value: 'colorTheme',
                    },
                    {
                        value: 'backgroundImage',
                    },
                    {
                        value: 'numberedCardBacks',
                    },
                    {
                        value: 'timeoutClosingCards',
                    },
                    ...PRIVACY_GAMIFICATION_DEMO,
                ],
            },
            ...TIMER_CONTROLS,
            ...GAMIFICATION_CONTROLS,
            ...ACTION_BUTTON_CONTROLS,
            ...LEAD_FORM_CONTROLS,
            {
                isVisible: () => hasPermission(CONDITIONS.CAN_USE_SDK_INTEGRATION),
                values: [{ value: 'integrationMetadata' }],
            },
        ],
        [BLOCK.cookies]: [
            {
                values: [{ value: 'colorTheme' }, { value: 'backgroundImage' }],
            },
            ...LEAD_FORM_CONTROLS,
            ...OLD_ACTION_BUTTON_CONTROLS,
        ],
        [BLOCK.horoscope]: [
            {
                values: [{ value: 'colorTheme' }, { value: 'backgroundImage' }],
            },
            ...LEAD_FORM_CONTROLS,
            ...OLD_ACTION_BUTTON_CONTROLS,
        ],
        [BLOCK.personalityQuiz]: [
            {
                values: [{ value: 'colorTheme' }],
            },
            {
                values: [{ value: 'progressBar' }],
            },
            ...LEAD_FORM_CONTROLS,
            ...OLD_ACTION_BUTTON_CONTROLS,
        ],
        [BLOCK.puzzle]: [
            {
                values: [
                    {
                        value: 'colorTheme',
                    },
                    ...PRIVACY_GAMIFICATION_DEMO,
                ],
            },
            ...TIMER_CONTROLS,
            ...GAMIFICATION_CONTROLS,
            ...ACTION_BUTTON_CONTROLS,
            ...LEAD_FORM_CONTROLS,
            {
                isVisible: () => hasPermission(CONDITIONS.CAN_USE_SDK_INTEGRATION),
                values: [{ value: 'integrationMetadata' }],
            },
        ],
        [BLOCK.treasureHunt]: [
            {
                values: [
                    {
                        value: 'colorTheme',
                    },
                ],
            },
            ...ACTION_BUTTON_CONTROLS,
            ...LEAD_FORM_CONTROLS,
            {
                isVisible: () => hasPermission(CONDITIONS.CAN_USE_SDK_INTEGRATION),
                values: [{ value: 'integrationMetadata' }],
            },
        ],
        [BLOCK.leadForm]: [
            {
                values: [
                    {
                        value: 'colorTheme',
                    },
                ],
            },
            ...ACTION_BUTTON_CONTROLS,
        ],
        [BLOCK.findPair]: [
            {
                values: [
                    {
                        value: 'colorTheme',
                    },
                    {
                        value: 'backgroundImage',
                    },
                    ...PRIVACY_GAMIFICATION_DEMO,
                ],
            },
            ...TIMER_CONTROLS,
            ...GAMIFICATION_CONTROLS,
            ...LEAD_FORM_CONTROLS,
            ...ACTION_BUTTON_CONTROLS,
            {
                isVisible: () => hasPermission(CONDITIONS.CAN_USE_SDK_INTEGRATION),
                values: [{ value: 'integrationMetadata' }],
            },
        ],
        [BLOCK.slideshow]: [
            {
                values: [{ value: 'colorTheme' }],
            },
        ],
    },
}

export function getControls({ target, typeIdentifier, blockData = {} }) {
    if (!target || !CONTROLS.hasOwnProperty(target)) return []

    const controls = typeIdentifier ? CONTROLS[target][typeIdentifier] : CONTROLS[target]
    if (controls) {
        return controls.map(group => {
            return {
                ...group,
                isVisible: group.isVisible ? group.isVisible(blockData) : true,
                values: group.values.map(value => {
                    return {
                        ...value,
                        isVisible: value.isVisible ? value.isVisible(blockData) : true,
                    }
                }),
                tooltipText: typeof group.tooltipText === 'function' ? group.tooltipText() : group.tooltipText,
            }
        })
    }
    return []
}
